import type { Action } from '../types';
import type { EnterpriseAction } from '../services/actionService';

const ENTERPRISE_STATUS_MAP: Record<string, Action['status']> = {
  pending: 'todo',
  in_progress: 'doing',
  completed: 'done',
  blocked: 'todo',
  cancelled: 'canceled',
};

/** Converte ação enterprise (Supabase) para o tipo Action usado no canvas/hub. */
export function enterpriseToAction(ea: EnterpriseAction): Action {
  const linkedNodeId = typeof ea.metadata?.linkedNodeId === 'string' ? ea.metadata.linkedNodeId : undefined;
  const source = String(ea.metadata?.source ?? ea.metadata?.generatedFrom ?? '');
  const assignedName = ea.assigned_name ?? (typeof ea.metadata?.assignedName === 'string' ? ea.metadata.assignedName : null);
  const metadataEffortRaw = ea.metadata?.matrixEffort;
  const metadataImpactRaw = ea.metadata?.matrixImpact;
  const metadataEffort = typeof metadataEffortRaw === 'number'
    ? metadataEffortRaw
    : typeof metadataEffortRaw === 'string' && metadataEffortRaw.trim() !== ''
      ? Number(metadataEffortRaw)
      : undefined;
  const metadataImpact = typeof metadataImpactRaw === 'number'
    ? metadataImpactRaw
    : typeof metadataImpactRaw === 'string' && metadataImpactRaw.trim() !== ''
      ? Number(metadataImpactRaw)
      : undefined;
  const originSource = linkedNodeId
    ? (source.includes('postit') || source.includes('idea') ? 'PostIt' : 'Canvas')
    : 'Manual';
  const effort = Number.isFinite(ea.effort as number)
    ? (ea.effort as number)
    : Number.isFinite(metadataEffort as number)
      ? metadataEffort
      : undefined;
  const impact = Number.isFinite(ea.impact as number)
    ? (ea.impact as number)
    : Number.isFinite(metadataImpact as number)
      ? metadataImpact
      : undefined;
  const effortLevel = effort == null
    ? undefined
    : effort <= 3
      ? (effort as 1 | 2 | 3)
      : effort < 33
        ? 1
        : effort < 66
          ? 2
          : 3;
  const impactLevel = impact == null
    ? undefined
    : impact <= 3
      ? (impact as 1 | 2 | 3)
      : impact < 33
        ? 1
        : impact < 66
          ? 2
          : 3;

  return {
    id: ea.id,
    title: ea.title,
    description: ea.description ?? undefined,
    status: ENTERPRISE_STATUS_MAP[ea.status] ?? 'todo',
    responsible: assignedName ?? undefined,
    responsavel: assignedName ?? undefined,
    responsavel_id: ea.assigned_to ?? (ea as { assigned_user_id?: string }).assigned_user_id ?? (ea as { user_id?: string }).user_id ?? (ea as { owner_id?: string }).owner_id ?? undefined,
    responsibleUserId: ea.assigned_to ?? (ea as { assigned_user_id?: string }).assigned_user_id ?? (ea as { user_id?: string }).user_id ?? undefined,
    deadline: ea.due_date ?? undefined,
    prazo: ea.due_date ?? undefined,
    projectId: ea.project_id ?? undefined,
    _projectName: ea.project_name ?? undefined,
    effort,
    impact,
    esforco: effortLevel,
    impacto: impactLevel,
    linkedNodeId,
    sourceComponentId: typeof ea.metadata?.sourceComponentId === 'string' ? ea.metadata.sourceComponentId : undefined,
    originSource,
    createdAt: ea.created_at,
    updatedAt: ea.updated_at,
  };
}

/** Mescla ações enterprise com patches locais do store (mesma lógica da jornada). */
export function mergeEnterpriseWithLocalActions(
  enterpriseActions: EnterpriseAction[],
  localActions: Action[],
): Action[] {
  const mapped = enterpriseActions.map(enterpriseToAction);
  if (mapped.length === 0) return localActions;

  const localById = new Map(localActions.map((action) => [action.id, action]));
  return mapped.map((enterprise) => {
    const local = localById.get(enterprise.id);
    if (!local) return enterprise;
    return {
      ...enterprise,
      ...local,
      priority: local.priority ?? enterprise.priority,
      impact: local.impact ?? enterprise.impact,
      effort: local.effort ?? enterprise.effort,
      impacto: local.impacto ?? enterprise.impacto,
      esforco: local.esforco ?? enterprise.esforco,
    };
  });
}
